import { useRef, useState } from "react";
import { format } from "date-fns";
import { nb } from "date-fns/locale";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { SrIcon } from "@/components/ui/SrIcon";
import { cn } from "@/lib/utils";
import {
  useTaskComments,
  useCreateTaskComment,
  useDeleteTaskComment,
  useSignedAttachmentUrl,
  type TaskComment,
} from "@/hooks/use-task-comments";
import { useImpersonation } from "@/contexts/ImpersonationContext";

function MiniAvatar({ user }: { user: TaskComment["user"] }) {
  const initials = (user?.full_name ?? "?")
    .split(" ")
    .map((n) => n[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
  return user?.avatar_url ? (
    <img
      src={user.avatar_url}
      alt={user.full_name}
      className="w-7 h-7 rounded-full object-cover ring-[1.5px] ring-foreground shrink-0"
    />
  ) : (
    <span className="w-7 h-7 rounded-full bg-card text-foreground text-[11px] font-medium flex items-center justify-center ring-[1.5px] ring-foreground shrink-0">
      {initials}
    </span>
  );
}

function formatBytes(bytes: number | null): string {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} kB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function isImage(type: string | null): boolean {
  return !!type && type.startsWith("image/");
}

/**
 * WP4: task-attachments-bucket er privat, så vi resolver en signert URL fra
 * den lagrede storage-pathen (attachment_url) før vi viser/lenker vedlegget.
 */
function CommentAttachment({ comment }: { comment: TaskComment }) {
  const { data: signedUrl, isLoading } = useSignedAttachmentUrl(comment.attachment_url);

  if (isLoading) {
    return (
      <div className="mt-1.5 text-[11.5px] text-muted-foreground italic">Laster vedlegg…</div>
    );
  }
  if (!signedUrl) {
    return (
      <div className="mt-1.5 inline-flex items-center gap-2 px-2.5 py-1.5 border-[1.5px] border-foreground rounded text-[12.5px] text-muted-foreground">
        <SrIcon name="paperclip" className="w-3.5 h-3.5" />
        <span className="truncate max-w-[200px]">{comment.attachment_name ?? "Vedlegg"}</span>
        <span>(utilgjengelig)</span>
      </div>
    );
  }

  return (
    <div className="mt-1.5">
      {isImage(comment.attachment_type) ? (
        <a href={signedUrl} target="_blank" rel="noopener noreferrer" className="inline-block">
          <img
            src={signedUrl}
            alt={comment.attachment_name ?? ""}
            className="max-w-[260px] max-h-[200px] object-cover rounded border-[1.5px] border-foreground"
          />
        </a>
      ) : (
        <a
          href={signedUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 px-2.5 py-1.5 border-[1.5px] border-foreground rounded text-[12.5px] hover:bg-muted"
        >
          <SrIcon name="paperclip" className="w-3.5 h-3.5" />
          <span className="truncate max-w-[200px]">{comment.attachment_name ?? "Vedlegg"}</span>
          {comment.attachment_size_bytes && (
            <span className="text-muted-foreground">{formatBytes(comment.attachment_size_bytes)}</span>
          )}
        </a>
      )}
    </div>
  );
}

interface TaskCommentsProps {
  assignmentId: string;
  /** Settes hvis komponenten ligger inni en read-only kontekst (eks. avsluttet prosjekt). */
  readOnly?: boolean;
}

export function TaskComments({ assignmentId, readOnly = false }: TaskCommentsProps) {
  const { activeUser } = useImpersonation();
  const { data: comments = [], isLoading } = useTaskComments(assignmentId);
  const createMut = useCreateTaskComment();
  const deleteMut = useDeleteTaskComment(assignmentId);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [body, setBody] = useState("");
  const [file, setFile] = useState<File | null>(null);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!activeUser?.id) return;
    createMut.mutate(
      { assignmentId, userId: activeUser.id, body, file },
      {
        onSuccess: () => {
          setBody("");
          setFile(null);
          if (fileInputRef.current) fileInputRef.current.value = "";
        },
      },
    );
  };

  return (
    <div className="border-[1.5px] border-foreground bg-card mt-2">
      <div className="px-3 py-2 border-b-[1.5px] border-foreground flex items-center gap-2">
        <SrIcon name="message-square" className="w-3.5 h-3.5 text-muted-foreground" />
        <span className="text-[11.5px] font-medium uppercase tracking-[0.08em] text-muted-foreground">
          Kommentarer
        </span>
        <span className="ml-auto text-[11.5px] text-muted-foreground tabular-nums">
          {comments.length}
        </span>
      </div>

      <div className="px-3 py-3 space-y-3 max-h-[360px] overflow-y-auto">
        {isLoading && (
          <div className="text-[12.5px] text-muted-foreground italic">Laster…</div>
        )}
        {!isLoading && comments.length === 0 && (
          <div className="text-[12.5px] text-muted-foreground italic">
            Ingen kommentarer enda.
          </div>
        )}
        {comments.map((c) => {
          const own = c.user_id === activeUser?.id;
          return (
            <div key={c.id} className="flex gap-2.5">
              <MiniAvatar user={c.user} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="text-[12.5px] font-medium">{c.user?.full_name ?? "Ukjent"}</span>
                  <span className="text-[11px] text-muted-foreground tabular-nums">
                    {format(new Date(c.created_at), "d. MMM HH:mm", { locale: nb })}
                  </span>
                  {own && !readOnly && (
                    <button
                      onClick={() => deleteMut.mutate(c.id)}
                      className="ml-auto text-[11px] text-muted-foreground hover:text-destructive"
                      title="Slett kommentar"
                    >
                      <SrIcon name="trash" className="w-3 h-3" />
                    </button>
                  )}
                </div>
                {c.body && (
                  <p className="text-[13px] whitespace-pre-wrap break-words">{c.body}</p>
                )}
                {c.attachment_url && <CommentAttachment comment={c} />}
              </div>
            </div>
          );
        })}
      </div>

      {!readOnly && activeUser && (
        <form onSubmit={handleSubmit} className="border-t-[1.5px] border-foreground p-2.5 space-y-2">
          <Textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Skriv en kommentar…"
            rows={2}
            className="text-[13px] min-h-[60px]"
          />
          <div className="flex items-center gap-2">
            <input
              ref={fileInputRef}
              type="file"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              className="hidden"
              id={`file-${assignmentId}`}
            />
            <label
              htmlFor={`file-${assignmentId}`}
              className="inline-flex items-center gap-1.5 text-[12.5px] text-muted-foreground hover:text-foreground cursor-pointer"
            >
              <SrIcon name="paperclip" className="w-3.5 h-3.5" />
              {file ? (
                <span className="truncate max-w-[180px] text-foreground">{file.name}</span>
              ) : (
                "Legg ved fil"
              )}
            </label>
            {file && (
              <button
                type="button"
                onClick={() => {
                  setFile(null);
                  if (fileInputRef.current) fileInputRef.current.value = "";
                }}
                className="text-muted-foreground hover:text-destructive"
                title="Fjern"
              >
                <SrIcon name="x" className="w-3.5 h-3.5" />
              </button>
            )}
            <span className="flex-1" />
            <Button
              type="submit"
              size="sm"
              className={cn("h-7 text-xs", createMut.isPending && "opacity-60")}
              disabled={createMut.isPending || (!body.trim() && !file)}
            >
              <SrIcon name="send" className="w-3 h-3" />
              Post
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}

export default TaskComments;
